import React, { useState, useRef, useEffect } from "react";
import "./PdfDisplay.css";
import { Modal } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";
import { GrSecure } from "react-icons/gr";
import { RiDownload2Line } from "react-icons/ri";
import { GrFormNext } from "react-icons/gr";
import { GrFormPrevious } from "react-icons/gr";
import { isDesktop } from "react-device-detect";
import { getAccessToken } from "../../util/authUtil";

const DOB_REGEX = /^(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])\d{2}$/;
const ERROR_MESSAGES = {
  DOB_REQUIRED: "Date of birth is required",
  INVALID_DOB: "Date of birth, please try again with correct date in MMDDYY format",
  INVALID_FORMAT: "Please enter your date of birth in MMDDYY format",
  ERROR_DOWNLOADING: "Error downloading file. Please try again. If the issue persists, please email hello@tbd.health",
};

const PdfDisplay = ({
  validatePwdCb,
  fileDisplayName,
  viewFileUrl,
  downloadFileUrl,
  handleClosePdf
}) => {
  const [errMsg, setErrMsg] = useState(null);
  const [password, setPassword] = useState(null);
  const [isShowPwdCollectionModal, setIsShowPwdCollectionModal] = useState(true);
  const [isShowPdfViewerModal, setIsShowPdfViewerModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [filePwdSignature, setFilePwdSignature] = useState(null);
  const errMsgRef = useRef(null);
  const passwordRef = useRef(null);
  passwordRef.current = password;

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { GoToNextPage, GoToPreviousPage } = pageNavigationPluginInstance;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const showPdfViewerModal = () => {
    setIsShowPwdCollectionModal(false);
    setIsShowPdfViewerModal(true);
  };

  useEffect(() => {
    setErrMsg(null);
  }, []);

  useEffect(() => {
    getAccessToken().then(token => setAccessToken(token));
  }, []);

  useEffect(() => {
    if (errMsgRef.current) {
      errMsgRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [errMsg]);

  const handlePwdSubmit = async () => {
    if (!password) {
      setErrMsg(ERROR_MESSAGES.DOB_REQUIRED);
      return;
    }
    if (!DOB_REGEX.test(password)) {
      setErrMsg(ERROR_MESSAGES.INVALID_FORMAT);
      return;
    }
    setErrMsg(null);
    const result = await validatePwdCb(password).then(r => r);
    const ok = result?.success === true || result === true;
    if (ok) {
      setFilePwdSignature(typeof result?.signature === "string" ? result.signature : null);
      const token = await getAccessToken();
      if (token && token !== accessToken) {
        setAccessToken(token);
      }
      showPdfViewerModal();
      return;
    }
    setErrMsg(ERROR_MESSAGES.INVALID_DOB);
  };

  const handleAskPassword = (e) => {
    const pwd = passwordRef.current || password;
    e.verifyPassword(pwd);
  };

  const handleClose = () => {
    setPdfData(null);
    setPdfLoadError(null);
    setFilePwdSignature(null);
    setPassword(null);
    setErrMsg(null);
    handleClosePdf();
  };

  // Fetch PDF as binary; send X-File-Pwd-Signature when backend returns it
  useEffect(() => {
    if (!isShowPdfViewerModal || !viewFileUrl) return;
    setPdfLoadError(null);
    setPdfData(null);
    let cancelled = false;
    const loadPdf = async () => {
      try {
        const token = accessToken || (await getAccessToken());
        if (!token) {
          setPdfLoadError("Session expired. Please log in again.");
          return;
        }
        const headers = {
          Authorization: `Bearer ${token}`,
          addresseeType: "P",
          ...(filePwdSignature ? { "X-File-Pwd-Signature": filePwdSignature } : {}),
        };
        const response = await fetch(viewFileUrl, { headers });
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status}`);
        }
        const blob = await response.blob();
        if (cancelled) return;
        const arrayBuffer = await blob.arrayBuffer();
        if (cancelled) return;
        setPdfData(new Uint8Array(arrayBuffer));
      } catch (err) {
        if (!cancelled) {
          setPdfLoadError(err.message || ERROR_MESSAGES.ERROR_DOWNLOADING);
        }
      }
    };
    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [isShowPdfViewerModal, viewFileUrl, accessToken, filePwdSignature]);

  async function handleDownloadClick() {
    if (isDownloading) return;
    setErrMsg(null);
    if (isDesktop) {
      await downloadFileWithHeadersDesktop()
        .then(res => res)
        .finally(() => setIsDownloading(false));
    } else {
      await downloadFileWithHeadersMobile()
        .then(res => res)
        .finally(() => setIsDownloading(false));
    }
  }

  async function downloadFileWithHeadersMobile() {
    setIsDownloading(true);
    try {
      const headers = new Headers();
      const authToken = accessToken || (await getAccessToken());
      if (!authToken) {
        setErrMsg("Session expired. Please log in again.");
        return;
      }
      headers.append("Authorization", `Bearer ${authToken}`);
      headers.append("addresseeType", "P");
      if (filePwdSignature) headers.append("X-File-Pwd-Signature", filePwdSignature);
      headers.append("content-type", "application/json");
      const response = await fetch(downloadFileUrl, { headers });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      if (navigator.userAgent.match(/safari/i)) {
        window.location.href = url;
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      setErrMsg(ERROR_MESSAGES.ERROR_DOWNLOADING);
      console.error("Error downloading file:", error);
    } finally {
      setIsDownloading(false);
    }
  }

  async function downloadFileWithHeadersDesktop() {
    setIsDownloading(true);
    try {
      const headers = new Headers();
      const authToken = accessToken || (await getAccessToken());
      if (!authToken) {
        setErrMsg("Session expired. Please log in again.");
        return;
      }
      headers.append("Authorization", `Bearer ${authToken}`);
      headers.append("addresseeType", "P");
      if (filePwdSignature) headers.append("X-File-Pwd-Signature", filePwdSignature);
      const response = await fetch(downloadFileUrl, { headers });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileDisplayName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrMsg(ERROR_MESSAGES.ERROR_DOWNLOADING);
      console.error("Error downloading file:", error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Modal
      show={true}
      onHide={handleClose}
      keyboard={false}
      onClick={e => e.stopPropagation()}
      animation={false}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      className="pdfModalBackdrop"
      centered
    >
      <Modal.Body className={isShowPdfViewerModal ? "pdfViewer-Modal" : null}>
        <div className="pdfModal-Container">
          {isShowPwdCollectionModal && (
            <div className="section1">
              <span className="close-x" onClick={handleClose}>X</span>
              <span style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                <span>
                  <GrSecure className="iconWhite" />
                  <div className="bold">
                    For your security, the results are encrypted.
                    <br />
                    Please enter your date of birth in the format MMDDYY
                  </div>
                  <div className="example">
                    For example, if your birthdate is May 11, 1987, enter 051187.
                  </div>
                </span>
              </span>
              <div className="section1-input">
                <div className="pdfRow">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="pdfInput"
                    value={password || ""}
                    required
                    onChange={e => setPassword(e.target.value)}
                  />
                  <span className="pdfInputIcon" onClick={togglePasswordVisibility}>
                    {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                  </span>
                </div>
                <button
                  className="pdf-proceed proceedButton"
                  onClick={handlePwdSubmit}
                  disabled={!password || !DOB_REGEX.test(password)}
                >
                  Proceed
                </button>
              </div>
              {errMsg && <b className="errMsg">{errMsg}</b>}
            </div>
          )}

          {isShowPdfViewerModal && (
            <>
              <span className="close-x-2" onClick={handleClose}>X</span>
              <div
                className="pdf-header"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flex: "1",
                  padding: "15px"
                }}
              >
                <span>
                  <div className="bold" style={{ fontSize: "1.8rem" }}>
                    <GrSecure className="iconWhite" /> Please enter your date of birth in MMDDYY format to open the downloaded file.
                  </div>
                  <div className="example">
                    For example, if your birthdate is May 11, 1987, enter 051187.
                  </div>
                </span>
              </div>
              <div>
                <div className="pdf-viewer-container">
                  <div className="pdf-viewer">
                    {pdfLoadError && (
                      <div className="errMsg" style={{ padding: "1rem" }}>{pdfLoadError}</div>
                    )}
                    {!pdfData && !pdfLoadError && (
                      <div style={{ padding: "2rem", textAlign: "center" }}>
                        <FaSpinner className="spinner" /> Loading PDF...
                      </div>
                    )}
                    {pdfData && !pdfLoadError && (
                      <Viewer
                        fileUrl={pdfData}
                        plugins={[pageNavigationPluginInstance]}
                        onDocumentAskPassword={handleAskPassword}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      alignItems: "center",
                      backgroundColor: "#eee",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      justifyContent: "center",
                      padding: "4px"
                    }}
                  >
                    <div style={{ padding: "5px 10px" }}>
                      <GoToPreviousPage>
                        {(props) => (
                          <button
                            type="button"
                            className="pdf-nav-button"
                            style={{
                              backgroundColor: props.isDisabled ? "rgba(238,129,85,0.5)" : "#EE8155",
                              border: "none",
                              color: "black",
                              borderRadius: "10vw",
                              cursor: props.isDisabled ? "not-allowed" : "pointer",
                              fontSize: "1.3rem",
                              padding: "2px 10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                            disabled={props.isDisabled}
                            onClick={props.onClick}
                          >
                            <GrFormPrevious />
                          </button>
                        )}
                      </GoToPreviousPage>
                    </div>
                    <div style={{ padding: "5px 10px" }}>
                      <GoToNextPage>
                        {(props) => (
                          <button
                            type="button"
                            className="pdf-nav-button"
                            style={{
                              backgroundColor: props.isDisabled ? "rgba(238,129,85,0.5)" : "#EE8155",
                              border: "none",
                              borderRadius: "10vw",
                              fontSize: "1.3rem",
                              color: "black",
                              cursor: props.isDisabled ? "not-allowed" : "pointer",
                              padding: "2px 10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                            disabled={props.isDisabled}
                            onClick={props.onClick}
                          >
                            <GrFormNext />
                          </button>
                        )}
                      </GoToNextPage>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <button
                  className="pdf-proceed proceedButton"
                  onClick={handleDownloadClick}
                  disabled={isDownloading}
                  style={{
                    backgroundColor: isDownloading ? "#F6C0AC" : "#ee8155",
                    marginTop: "20px"
                  }}
                >
                  {isDownloading ? (
                    <><FaSpinner className="spinner" />&nbsp;Downloading...</>
                  ) : (
                    <>Download&nbsp;&nbsp;<RiDownload2Line /></>
                  )}
                </button>
              </div>
              {errMsg && (
                <div className="errMsg" ref={errMsgRef}>{errMsg}</div>
              )}
              <br />
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PdfDisplay;
